// pages/Services.js
import React, { useState } from 'react';
import { ContentLayout, PageContainer, ContentSection } from '../components/layout';
import { Card, CardContent, Button, Badge, StatusIndicator, Input, Modal, Select, TextArea, Loading, ErrorMessage } from '../components/common';
import { Service } from '../models';
import { useServicesPG } from '../hooks/useServicesPG';
import { useTranslations } from '../hooks/useTranslations';
import { useBusinessConfig } from '../context/BusinessConfigContext';
import { useServiceCategories } from '../hooks/useServiceCategories';
import { PlusCircle, Settings } from 'lucide-react';

// Category Management Modal Component
function CategoryManagementModal({ isOpen, onClose, onCategoryChange }) {
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    icon: '🔹'
  });
  const [deleteError, setDeleteError] = useState(null);
  const [affectedServices, setAffectedServices] = useState([]);

  const { service_categories, refreshConfig } = useBusinessConfig();
  const { addCategory, updateCategory, deleteCategory, loading, error, clearError } = useServiceCategories();

  const CATEGORY_ICONS = ['🔹', '💅', '✨', '💆', '🧖', '💇', '🌟', '💎', '🌸', '🌺', '🎨', '🪒', '👁️‍🗨️', '🗨️', '🎓', '🌿'];

  React.useEffect(() => {
    if (isOpen && service_categories) {
      setCategories(service_categories);
      setIsAddingNew(false);
      setEditingCategory(null);
      setDeleteError(null);
      setAffectedServices([]);
      clearError();
    }
  }, [isOpen, service_categories, clearError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStartAdd = () => {
    setFormData({
      value: '',
      label: '',
      icon: '🔹'
    });
    setIsAddingNew(true);
    setEditingCategory(null);
    setDeleteError(null);
    setAffectedServices([]);
  };

  const handleStartEdit = (category) => {
    setFormData({
      value: category.value,
      label: category.label,
      icon: category.icon || '🔹'
    });
    setEditingCategory(category.value);
    setIsAddingNew(false);
    setDeleteError(null);
    setAffectedServices([]);
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setIsAddingNew(false);
    setFormData({
      value: '',
      label: '',
      icon: '🔹'
    });
    setDeleteError(null);
    setAffectedServices([]);
  };

  const handleSaveAdd = async () => {
    try {
      const result = await addCategory(formData);
      setCategories(result.allCategories);
      setIsAddingNew(false);
      setFormData({ value: '', label: '', icon: '🔹' });
      await refreshConfig();
      onCategoryChange?.();
    } catch (err) {
      console.error('Error adding category:', err);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const updates = {
        label: formData.label,
        icon: formData.icon
      };
      const result = await updateCategory(editingCategory, updates);
      setCategories(result.allCategories);
      setEditingCategory(null);
      setFormData({ value: '', label: '', icon: '🔹' });
      await refreshConfig();
      onCategoryChange?.();
    } catch (err) {
      console.error('Error updating category:', err);
    }
  };

  const handleDelete = async (categoryValue) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק קטגוריה זו?')) {
      return;
    }

    try {
      setDeleteError(null);
      setAffectedServices([]);
      const result = await deleteCategory(categoryValue);
      setCategories(result.allCategories);
      await refreshConfig();
      onCategoryChange?.();
    } catch (err) {
      console.error('Error deleting category:', err);
      if (err.affectedServices) {
        setDeleteError(err.message);
        setAffectedServices(err.affectedServices);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title='ניהול קטגוריות שירותים'
      size="large"
    >
      <div className="space-y-4">
        {/* Error Message */}
        {(error || deleteError) && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0 text-red-400">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="mr-3 flex-1">
                <p className="text-sm">{error || deleteError}</p>
                {affectedServices.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium mb-1">שירותים מושפעים:</p>
                    <ul className="list-disc list-inside text-sm">
                      {affectedServices.map(service => (
                        <li key={service.id}>{service.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add New Category Button */}
        {!isAddingNew && !editingCategory && (
          <Button
            onClick={handleStartAdd}
            variant="primary"
            size="small"
            className="mb-4"
          >
            <PlusCircle className="w-4 h-4 ml-2" />
            {'הוסף קטגוריה חדשה'}
          </Button>
        )}

        {/* Add/Edit Form */}
        {(isAddingNew || editingCategory) && (
          <Card className="mb-4 bg-blue-50">
            <CardContent>
              <h4 className="font-medium text-gray-900 mb-4">
                {isAddingNew ? 'הוסף קטגוריה חדשה' : 'ערוך קטגוריה'}
              </h4>
              <div className="space-y-4">
                {isAddingNew && (
                  <Input
                    label='ערך קטגוריה'
                    name="value"
                    value={formData.value}
                    onChange={handleInputChange}
                    placeholder="למשל: facials"
                    required
                    hint='מזהה ייחודי (ללא רווחים)'
                  />
                )}
                <Input
                  label='שם קטגוריה'
                  name="label"
                  value={formData.label}
                  onChange={handleInputChange}
                  placeholder="למשל: טיפולי פנים"
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    אייקון
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORY_ICONS.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        className={`w-10 h-10 rounded border-2 transition-all hover:scale-110 ${
                          formData.icon === icon
                            ? 'border-blue-500 bg-blue-100'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setFormData(prev => ({ ...prev, icon }))}
                      >
                        <span className="text-xl">{icon}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-4">
                <Button
                  variant="secondary"
                  onClick={handleCancelEdit}
                  size="small"
                  className="ml-2"
                >
                  ביטול
                </Button>
                <Button
                  variant="primary"
                  onClick={isAddingNew ? handleSaveAdd : handleSaveEdit}
                  size="small"
                  loading={loading}
                  disabled={!formData.label || (isAddingNew && !formData.value)}
                >
                  שמור
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Categories List */}
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900 mb-3">
            קטגוריות קיימות ({categories.length})
          </h4>
          {categories.map((category) => (
            <Card key={category.value} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 ml-3 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">{category.icon}</span>
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">{category.label}</h5>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => handleStartEdit(category)}
                      disabled={editingCategory === category.value || isAddingNew}
                    >
                      ערוך
                    </Button>
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => handleDelete(category.value)}
                      disabled={editingCategory || isAddingNew || loading}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      מחק
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Close Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <Button
            variant="primary"
            onClick={onClose}
          >
            סגור
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Service Modal Component (for both Add and Edit)
function ServiceModal({ isOpen, onClose, onSuccess, service = null, mode = 'add' }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    duration: '',
    price: '',
    color: '#F8BBD9',
    is_active: true,
    is_visible_to_customers: true,
    show_price_to_customers: true
  });

const SERVICE_COLORS = [
  '#D32F2F', // Strong Red
  '#1976D2', // Vivid Blue
  '#388E3C', // Rich Green
  '#F57C00', // Bold Orange
  '#7B1FA2', // Strong Purple
  '#0288D1', // Cyan-Teal
  '#C2185B', // Deep Pink
  '#512DA8', // Indigo Purple
  '#00796B', // Teal
  '#455A64', // Blue Grey
  '#AFB42B', // Olive Green
  '#5D4037', // Brown
  '#303F9F', // Dark Indigo
  '#0097A7', // Dark Cyan
  '#E64A19', // Burnt Orange
  '#8E24AA', // Vivid Violet
  '#33691E', // Dark Lime Green
  '#BF360C'  // Deep Rust Orange
];

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const t = useTranslations();

  const { service_categories } = useBusinessConfig();

  // Reset form when modal opens/closes or service changes
  React.useEffect(() => {
    if (isOpen) {
      if (service && mode === 'edit') {
        // Populate form with existing service data
        setFormData({
          name: service.name || '',
          description: service.description || '',
          category: service.category || '',
          duration: service.duration?.toString() || '',
          price: service.price?.toString() || '',
          color: service.color || '#F8BBD9',
          is_active: service.is_active !== undefined ? service.is_active : true,
          is_visible_to_customers: service.is_visible_to_customers !== undefined ? service.is_visible_to_customers : true,
          show_price_to_customers: service.show_price_to_customers !== undefined ? service.show_price_to_customers : true

        });
      } else {
        // Reset form for new service
        setFormData({
          name: '',
          description: '',
          category: '',
          duration: '',
          price: '',
          color: '#F8BBD9',
          is_active: true,
          is_visible_to_customers: true,
          show_price_to_customers: true
        });
      }
      setErrors({});
      setSubmitSuccess(false);
    }
  }, [isOpen, service, mode]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      // If is_visible_to_customers is unchecked, also uncheck show_price_to_customers
      if (name === 'is_visible_to_customers' && !checked) {
        newData.show_price_to_customers = false;
      }

      return newData;
    });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    // Convert strings to numbers for validation
    const validationData = {
      ...formData,
      duration: parseInt(formData.duration) || 0,
      price: parseFloat(formData.price) || 0
    };

    const validation = Service.validate(validationData);
    const fieldErrors = {};

    if (!validation.isValid) {
      validation.errors.forEach(error => {
        if (error.includes('name')) fieldErrors.name = error;
        if (error.includes('Duration')) fieldErrors.duration = error;
        if (error.includes('Price')) fieldErrors.price = error;
        if (error.includes('category')) fieldErrors.category = error;
      });
    }

    setErrors(fieldErrors);
    return validation.isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const serviceData = {
        ...formData,
        duration: parseInt(formData.duration),
        price: parseFloat(formData.price)
      };

      if (mode === 'edit') {
        // Update existing service using Firestore
        await onSuccess(service.id, serviceData, 'edit');
      } else {
        // Create new service using Firestore
        await onSuccess(serviceData, 'add');
      }

      setSubmitSuccess(true);

      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const categoryOptions = Service.getCategoriesForSelect(service_categories || [], t);

  const modalTitle = mode === 'edit' ? t.editService : t.addNewService;
  const successMessage = mode === 'edit' ? t.serviceUpdatedSuccessfully : t.serviceAddedSuccessfully;
  const successDescription = mode === 'edit' ? t.serviceInformationHasBeenUpdated : t.newServiceHasBeenAddedToMenu;
  const submitButtonText = mode === 'edit'
    ? (isSubmitting ? t.updatingService : t.updateService)
    : (isSubmitting ? t.addingService : t.addService);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="medium"
    >
      {submitSuccess ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{successMessage}</h3>
          <p className="text-gray-600">{successDescription}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Show service info header for edit mode */}
          {mode === 'edit' && service && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">{Service.getCategoryIcon(service.category, service_categories)}</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{service.name}</h4>
                  <p className="text-sm text-gray-500">
                    {t[service.category] || service.category} • {t.currencySymbol}{service.price} • {service.duration} {t.minutes}
                  </p>
                </div>
              </div>
            </div>
          )}

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0 text-red-400">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{errors.submit}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label={t.serviceName}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t.enterServiceName}
                required
                error={errors.name}
                leftIcon="✨"
              />
            </div>

            <div className="md:col-span-1">
              <Select
                label={t.category}
                name="category"
                value={formData.category || ''}
                onChange={handleInputChange}
                options={categoryOptions}
                placeholder={t.selectACategory}
                required
                error={errors.category}
                hint={t.chooseTheServiceCategory}
              />
            </div>
            <div className="md:col-span-1">
              <div className="flex space-x-4">
                <div className="flex-1">
                  <Input
                    label={t.durationMinutes}
                    name="duration"
                    type="number"
                    value={formData.duration}
                    onChange={handleInputChange}
                    placeholder="60"
                    required
                    error={errors.duration}
                    leftIcon="⏱️"
                  />
                </div>
              </div>

              <div className="flex-1">
                <Input
                  label={t.priceILS}
                  name="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="150.00"
                  required
                  error={errors.price}
                  leftIcon="💰"
                />
              </div>
            </div>

            {/* Service Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.serviceColor || 'Service Color'}
              </label>
              <div className="space-y-3">
                {/* Selected Color Preview */}
                <div className="flex items-center space-x-3">
                  <div
                    className="w-8 h-8 rounded-full border-2 border-gray-200 shadow"
                    style={{ backgroundColor: formData.color }}
                  ></div>
                  <span className="text-sm text-gray-600">{formData.color}</span>
                </div>

                {/* Color Palette */}
                <div className="grid grid-cols-6 gap-2">
                  {SERVICE_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${formData.color === color
                        ? 'border-gray-400 ring-2 ring-gray-300'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              {/* Service Visibility Settings */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-4">
                  הגדרות נראות שירות
                </h4>

                <div className="space-y-4">
                  {/* Customer Visibility Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center ml-3">
                      <input
                        type="checkbox"
                        name="is_visible_to_customers"
                        checked={formData.is_visible_to_customers}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700">
                        גלוי ללקוחות
                      </label>
                      <p className="text-xs text-gray-500">
                        האם השירות יופיע ללקוחות בתהליך הזמנת התור
                      </p>
                    </div>
                  </div>

                  {/* Price Visibility Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center ml-3">
                      <input
                        type="checkbox"
                        name="show_price_to_customers"
                        checked={formData.show_price_to_customers}
                        onChange={handleInputChange}
                        disabled={!formData.is_visible_to_customers}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded disabled:opacity-50"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700">
                        הצג מחיר ללקוחות
                      </label>
                      <p className="text-xs text-gray-500">
                        האם המחיר יוצג ללקוחות (רלוונטי רק אם השירות גלוי ללקוחות)
                      </p>
                    </div>
                  </div>

                  {/* Warning message when service is not visible to customers */}
                  {!formData.is_visible_to_customers && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <div className="flex">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="mr-3">
                          <h3 className="text-sm font-medium text-yellow-800">
                            שירות פנימי
                          </h3>
                          <div className="text-sm text-yellow-700">
                            השירות הזה יהיה זמין רק לצוות ולא יופיע ללקוחות בתהליך הזמנת התור.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <TextArea
                label={t.serviceDescription}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t.describeWhatThisServiceIncludes}
                rows={3}
                hint={t.helpCustomersUnderstand}
              />
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="mr-3 block text-sm text-gray-900">
                  {t.serviceActive}
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">{t.enableToShowInBookings}</p>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
              className='ml-3'
            >
              {t.cancel}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {submitButtonText}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function Services({ onNavigate }) {
  const [filter, setFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedService, setSelectedService] = useState(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const t = useTranslations();

  // Use Firestore services hook instead of local state
  const {
    services,
    loading,
    error,
    addService,
    updateService,
    toggleServiceStatus,
    clearError
  } = useServicesPG();

  const { service_categories } = useBusinessConfig();
  const categories = Service.getCategoriesWithAll(service_categories, t);
  const categoryColors = Service.getCategoryColors(service_categories);

  const filteredServices = filter === 'all'
    ? services
    : services.filter(service => service.category === filter);

  const handleAddService = () => {
    setModalMode('add');
    setSelectedService(null);
    setIsModalOpen(true);
  };

  const handleServiceClick = (service) => {
    setModalMode('edit');
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleServiceSubmit = async (serviceDataOrId, modeOrData, mode) => {
    try {
      if (mode === 'edit') {
        // serviceDataOrId is the ID, modeOrData is the update data
        await updateService(serviceDataOrId, modeOrData);
        console.log('Service updated successfully');
      } else {
        // serviceDataOrId is the new service data
        await addService(serviceDataOrId);
        console.log('Service added successfully');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      // Error is already handled by the hook
    }
  };

  const handleToggleStatus = async (serviceId, e) => {
    e.stopPropagation(); // Prevent card click
    try {
      await toggleServiceStatus(serviceId);
      console.log('Service status toggled successfully');
    } catch (error) {
      console.error('Error toggling service status:', error);
      // Error is already handled by the hook
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) {
      return `${mins} ${t.minutes}`;
    } else if (mins === 0) {
      return `${hours} ${hours > 1 ? t.hours : t.hour}`;
    } else {
      return `${hours} ${hours > 1 ? t.hours : t.hour} ${mins} ${t.minutes}`;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <ContentLayout
        currentPage="services"
        onNavigate={onNavigate}
        title={t.serviceManagement}
        description={t.manageYourBeautyServiceOfferings}
      >
        <PageContainer>
          <Loading text={t.loadingServices} />
        </PageContainer>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      currentPage="services"
      onNavigate={onNavigate}
      title={t.serviceManagement}
      description={t.manageYourBeautyServiceOfferings}
      primaryAction={{
        label: t.addService,
        onClick: handleAddService,
        icon: <PlusCircle className="w-4 h-4" />
      }}
    >
      <PageContainer>
        {/* Error handling */}
        {error && (
          <ErrorMessage
            message={error}
            dismissible
            onDismiss={clearError}
            className="mb-6"
          />
        )}

        {/* Category Filter */}
        <Card className="mb-6">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">{'סנן לפי קטגוריה'}</h3>
              <Button
                variant="ghost"
                size="small"
                onClick={() => setIsCategoryModalOpen(true)}
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
              >
                <Settings className="w-4 h-4 ml-2" />
                {'ערוך קטגוריות'}
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              {categories.map((category) => (
                <Button
                  key={category.value}
                  variant={filter === category.value ? 'primary' : 'secondary'}
                  size="small"
                  onClick={() => setFilter(category.value)}
                  className="
            px-2.5 py-1 md:px-3 md:py-1.5
            text-[12px] md:text-sm
            rounded-full
            leading-5
            shadow-sm
          "
                >
                  <span className="text-sm md:text-base"> {category.icon} </span>
                  <span className="ms-1">{category.label}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>


        {/* Services Header */}
        <ContentSection
          title={`${filter === 'all' ? t.allServices : categories.find(c => c.value === filter)?.label} (${filteredServices.length})`}
          description={filter === 'all' ? t.allYourAvailableServices : `${t.servicesInTheCategory} ${filter === 'all' ? '' : t[filter] || filter}`}
        />

        {/* Services Grid */}
        {filteredServices.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <Card
                key={service.id}
                hoverable
                onClick={() => handleServiceClick(service)}
                className="cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-pink-200"
              >
                <CardContent>
                  {/* Service Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-3">
                        <span className="text-2xl">{Service.getCategoryIcon(service.category, service_categories)}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{service.name}</h3>
                        <Badge variant={categoryColors[service.category]} size="small">
                          {t[service.category] || service.category}
                        </Badge>
                      </div>
                    </div>
                    {/* Edit indicator */}
                    <div className="text-gray-400 hover:text-pink-500 transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                  </div>

                  {/* Duration and Price */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="mr-1">⏱️</span>
                      {formatDuration(service.duration)}
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {t.currencySymbol}{service.price}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                    {service.description}
                  </p>

                  {/* Status and Actions */}
                  <div className="flex items-center justify-between">
                    <StatusIndicator
                      status={service.is_active ? 'active' : 'inactive'}
                      size="small"
                    />
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={(e) => handleToggleStatus(service.id, e)}
                    >
                      {service.is_active ? t.deactivate : t.activate}
                    </Button>
                  </div>

                  {/* Click to edit hint */}
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 text-center">{t.clickToEditServiceDetails}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Empty State */
          <Card>
            <CardContent>
              <div className="text-center py-12">
                <div className="text-6xl mb-4">✨</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {filter === 'all' ? t.noServicesYet : `${t.noServicesInCategory} ${t[filter] || filter}`}
                </h3>
                <p className="text-gray-600 mb-4">
                  {filter === 'all'
                    ? t.getStartedByAddingFirstService
                    : `${t.addServicesToCategory} ${t[filter] || filter}`
                  }
                </p>
                <Button onClick={handleAddService}>
                  {filter === 'all' ? t.addYourFirstService : `${t.addServiceToCategory}${t[filter] || filter}`}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Service Modal */}
        <ServiceModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={handleServiceSubmit}
          service={selectedService}
          mode={modalMode}
        />

        {/* Category Management Modal */}
        <CategoryManagementModal
          isOpen={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          onCategoryChange={() => {
            // Optionally refresh services list or perform other actions
            console.log('Categories updated');
          }}
        />
      </PageContainer>
    </ContentLayout>
  );
}